import {
  Box,
  Stack,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  AlertTitle,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/apiClient';
import { SystemHealth } from '../types';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from 'react-i18next';

export default function HealthDashboardPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { token } = useAuth();
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getSystemHealth(token);
      setHealth(result);
    } catch (err: unknown) {
      setError((err as Error)?.message || t('healthDashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const formatUptime = (seconds: number) => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    parts.push(`${m}m`);
    return parts.join(' ');
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'healthy': return theme.palette.success.main;
      case 'degraded': return theme.palette.warning.main;
      case 'unhealthy': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  if (loading && !health) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>{t('healthDashboard.title')}</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error && !health) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>{t('healthDashboard.title')}</Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!health) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('healthDashboard.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('pageSubtitle.health')}
      </Typography>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('healthDashboard.apiStatus')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: statusColor(health.status),
                  }}
                />
                <Typography variant="h5">
                  {health.status === 'healthy'
                    ? t('healthDashboard.healthy')
                    : health.status === 'degraded'
                      ? t('healthDashboard.degraded')
                      : t('healthDashboard.unhealthy')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('healthDashboard.dbStatus')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: health.database === 'connected'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  }}
                />
                <Typography variant="h5">
                  {health.database === 'connected'
                    ? t('healthDashboard.connected')
                    : t('healthDashboard.unreachable')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('healthDashboard.uptime')}
              </Typography>
              <Typography variant="h5" sx={{ mt: 1 }}>
                {formatUptime(health.uptime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                v{health.version}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {health.counts.tenantsInGrace > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>{t('healthDashboard.gracePeriodAlert')}</AlertTitle>
          {t('healthDashboard.gracePeriodAlertDesc', { count: health.counts.tenantsInGrace })}
        </Alert>
      )}

      {health.counts.staleDevices > 0 && (
        <Alert severity={health.counts.staleDevices > 10 ? 'warning' : 'info'} sx={{ mb: 3 }}>
          <AlertTitle>{t('healthDashboard.staleDevicesAlert')}</AlertTitle>
          {t('healthDashboard.staleDevicesAlertDesc', { count: health.counts.staleDevices })}
        </Alert>
      )}

      {/* Tenants in Grace Period */}
      {health.details.tenantsInGrace.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('healthDashboard.tenantsInGrace')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('healthDashboard.name')}</TableCell>
                  <TableCell>{t('healthDashboard.dealer')}</TableCell>
                  <TableCell>{t('healthDashboard.licenseExpired')}</TableCell>
                  <TableCell>{t('healthDashboard.graceEnds')}</TableCell>
                  <TableCell>{t('healthDashboard.daysRemaining')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {health.details.tenantsInGrace.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.dealerName}</TableCell>
                    <TableCell>{new Date(t.licenseValidUntil).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(t.graceEndsAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${t.daysRemaining}d`}
                        color={t.daysRemaining <= 3 ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Stale Devices */}
      {health.details.staleDevices.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('healthDashboard.staleDevicesTitle')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('healthDashboard.tenant')}</TableCell>
                  <TableCell>{t('healthDashboard.device')}</TableCell>
                  <TableCell>{t('healthDashboard.platform')}</TableCell>
                  <TableCell>{t('healthDashboard.lastSeen')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {health.details.staleDevices.map(d => (
                  <TableRow key={d.id}>
                    <TableCell>{d.tenantName}</TableCell>
                    <TableCell>{d.deviceName || d.deviceId}</TableCell>
                    <TableCell>{d.platform || '-'}</TableCell>
                    <TableCell>{new Date(d.lastSeenAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

    </Box>
  );
}
